import { cn } from '@/lib/utils';
import { useUIStore } from '@/store';
import { useDappBuilderStore } from '@/store/dappBuilderStore';
import { useExtensionStore } from '@/store/extensionStore';
import { useWalletStore } from '@/store/walletStore';
import {
    Activity,
    Brain,
    Bug,
    FileCode2,
    FolderOpen,
    GitBranch,
    LayoutDashboard,
    Play,
    Puzzle,
    Rocket,
    ScrollText,
    Search,
    Settings,
    Shield,
    Sparkles,
    Wallet,
} from 'lucide-react';
import React from 'react';

type PanelId = string; // Now supports dynamic extension panel IDs

interface ActivityItem {
  id: PanelId;
  icon: React.ElementType;
  label: string;
}

const coreItems: ActivityItem[] = [
  { id: 'explorer', icon: FolderOpen, label: 'Explorer' },
  { id: 'search', icon: Search, label: 'Search' },
  { id: 'git', icon: GitBranch, label: 'Source Control' },
  { id: 'deploy', icon: Rocket, label: 'Deploy' },
  { id: 'contracts', icon: FileCode2, label: 'Contracts' },
  { id: 'ai', icon: Sparkles, label: 'AI Assistant' },
  { id: 'ai-agent', icon: Brain, label: 'AI Agent' },
  { id: 'secrets', icon: Shield, label: 'Secrets & Env' },
  { id: 'logs', icon: ScrollText, label: 'Logs' },
  { id: 'debug', icon: Bug, label: 'RPC Debugger' },
  { id: 'health', icon: Activity, label: 'Health Monitor' },
];

export default function ActivityBar() {
  const { activePanel, setActivePanel } = useUIStore();
  const { isConnected: walletConnected } = useWalletStore();
  const extensionPanels = useExtensionStore((s) => s.panels);

  // Build dynamic items: core items + extension panels + settings/extensions at bottom-ish
  const extensionItems: ActivityItem[] = extensionPanels.map((p) => ({
    id: `ext:${p.extensionId}:${p.id}`,
    icon: typeof p.icon === 'string' ? Puzzle : p.icon,
    label: p.label,
  }));

  const allItems = [...coreItems, ...extensionItems];

  const handleClick = (id: PanelId) => {
    setActivePanel(id as Parameters<typeof setActivePanel>[0]);
  };

  return (
    <div className="flex flex-col items-center w-12 bg-[#333333] border-r border-[#252525] py-1 shrink-0 select-none">
      {allItems.map((item) => {
        const Icon = item.icon;
        const isActive = activePanel === item.id;

        return (
          <button
            key={item.id}
            onClick={() => handleClick(item.id)}
            className={cn(
              'relative flex items-center justify-center w-12 h-12 text-[#858585] hover:text-white transition-colors group',
              isActive && 'text-white',
            )}
            title={item.label}
          >
            {/* Active indicator */}
            {isActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-white rounded-r" />
            )}

            <Icon className="w-5 h-5" />

            {/* Tooltip */}
            <div className="absolute left-14 px-2 py-1 bg-[#252525] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
              {item.label}
            </div>
          </button>
        );
      })}

      {/* Spacer to push bottom buttons */}
      <div className="flex-1" />

      {/* Create dApp button */}
      <button
        onClick={() => useDappBuilderStore.getState().open()}
        className="relative flex items-center justify-center w-12 h-12 text-[#858585] hover:text-white transition-colors group"
        title="Create dApp"
      >
        <LayoutDashboard className="w-5 h-5" />
        <div className="absolute left-14 px-2 py-1 bg-[#252525] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          Create dApp
        </div>
      </button>

      {/* Extensions button */}
      <button
        onClick={() => handleClick('extensions')}
        className={cn(
          'relative flex items-center justify-center w-12 h-12 text-[#858585] hover:text-white transition-colors group',
          activePanel === 'extensions' && 'text-white',
        )}
        title="Extensions"
      >
        {activePanel === 'extensions' && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-white rounded-r" />
        )}
        <Puzzle className="w-5 h-5" />
        {extensionPanels.length > 0 && (
          <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-blue-400" />
        )}
        <div className="absolute left-14 px-2 py-1 bg-[#252525] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          Extensions
        </div>
      </button>

      {/* Settings button */}
      <button
        onClick={() => handleClick('settings')}
        className={cn(
          'relative flex items-center justify-center w-12 h-12 text-[#858585] hover:text-white transition-colors group',
          activePanel === 'settings' && 'text-white',
        )}
        title="Settings"
      >
        {activePanel === 'settings' && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-white rounded-r" />
        )}
        <Settings className="w-5 h-5" />
        <div className="absolute left-14 px-2 py-1 bg-[#252525] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          Settings
        </div>
      </button>

      {/* Wallet button */}
      <button
        onClick={() => handleClick('wallet')}
        className={cn(
          'relative flex items-center justify-center w-12 h-12 transition-colors group',
          activePanel === 'wallet'
            ? 'text-white'
            : walletConnected
              ? 'text-green-400'
              : 'text-[#858585] hover:text-white',
        )}
        title={walletConnected ? 'Wallet Connected' : 'Connect Wallet'}
      >
        {activePanel === 'wallet' && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-white rounded-r" />
        )}
        <Wallet className="w-5 h-5" />
        {walletConnected && (
          <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-green-400" />
        )}
        <div className="absolute left-14 px-2 py-1 bg-[#252525] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          {walletConnected ? 'Wallet Connected' : 'Connect Wallet'}
        </div>
      </button>

      {/* Run button */}
      <button
        onClick={() => {}}
        className="flex items-center justify-center w-12 h-12 text-[#858585] hover:text-green-400 transition-colors group"
        title="Run"
      >
        <Play className="w-5 h-5" />
        <div className="absolute left-14 px-2 py-1 bg-[#252525] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          Run
        </div>
      </button>
    </div>
  );
}
